/**
 * Subscription Service
 *
 * Manages subscription state between backend (source of truth) and Jazz (cached for offline).
 * Provides limit checking and usage tracking for the freemium model.
 *
 * Tier Limits:
 * - Free: 3 lists, 7-day session history
 * - Plus ($9.99/yr): 30 lists, 30-day session history
 * - Premium ($19.99/yr): 300 lists, 1-year session history
 * - Enterprise: Unlimited (contact sales)
 *
 * Beta Mode:
 * When subscription status is 'beta', users get Plus tier limits regardless of their
 * actual tier. This allows soft enforcement during beta testing.
 */

import {
  DEFAULT_TIER_LIMITS,
  getEffectiveTier as getEffectiveTierFromBilling,
  getTierDisplayName,
  isPaidTier,
} from '@jbr-jazz/billing-shared';
import { walkTree } from '@jbr-jazz/hierarchy-shared';
import type { InstanceOfSchema } from 'jazz-tools';
import { isTemplateFolder } from '../hooks';
import {
  type AccountParam,
  type FolderNode,
  type SubscriptionStatus,
  type SubscriptionTier,
  UserSettings,
} from '../schema';

// Re-export display helpers from billing-shared
export { getTierDisplayName, isPaidTier };

// ============================================================================
// Types
// ============================================================================

export interface TierLimits {
  maxLists: number;
  sessionRetentionDays: number;
}

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  endsAt: number | null;
  limits: TierLimits;
  syncedAt: number | null;
}

// ============================================================================
// Tier Configuration (Single Source of Truth)
// ============================================================================

export interface TierConfig {
  slug: SubscriptionTier;
  name: string;
  priceCents: number;
  priceDisplay: string;
  maxLists: number;
  maxListsDisplay: string;
  sessionRetentionDays: number;
  sessionRetentionDisplay: string;
}

export const TIERS: Record<SubscriptionTier, TierConfig> = {
  free: {
    slug: 'free',
    name: 'Free',
    priceCents: 0,
    priceDisplay: 'Free',
    maxLists: 3,
    maxListsDisplay: '3',
    sessionRetentionDays: 7,
    sessionRetentionDisplay: '7 days',
  },
  plus: {
    slug: 'plus',
    name: 'Plus',
    priceCents: 999,
    priceDisplay: '$9.99/year',
    maxLists: 30,
    maxListsDisplay: '30',
    sessionRetentionDays: 30,
    sessionRetentionDisplay: '30 days',
  },
  premium: {
    slug: 'premium',
    name: 'Premium',
    priceCents: 1999,
    priceDisplay: '$19.99/year',
    maxLists: 300,
    maxListsDisplay: '300',
    sessionRetentionDays: 365,
    sessionRetentionDisplay: '1 year',
  },
  enterprise: {
    slug: 'enterprise',
    name: 'Enterprise',
    priceCents: 0,
    priceDisplay: 'Contact sales',
    maxLists: -1,
    maxListsDisplay: 'Unlimited',
    sessionRetentionDays: -1,
    sessionRetentionDisplay: 'Unlimited',
  },
};

// Enforcement limits come from billing-shared (single source of truth for the
// numbers); TIERS above keeps only CheckList's display strings and pricing.
export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = Object.fromEntries(
  Object.entries(DEFAULT_TIER_LIMITS).map(([key, raw]) => [
    key,
    { maxLists: raw.maxItems, sessionRetentionDays: raw.retentionDays },
  ]),
) as Record<SubscriptionTier, TierLimits>;

// Sync interval: 1 hour
const SYNC_INTERVAL_MS = 60 * 60 * 1000;

// ============================================================================
// Subscription State (from Jazz cache)
// ============================================================================

/**
 * Get current subscription tier (from Jazz cache)
 * Note: This returns the user's actual tier, not the effective tier.
 * Use getEffectiveTier() for limit calculations during beta.
 */
export function getSubscriptionTier(account: AccountParam): SubscriptionTier {
  const userSettings = account?.root?.userSettings;
  return (userSettings?.subscriptionTier as SubscriptionTier) ?? 'free';
}

/**
 * Get current subscription status (from Jazz cache)
 */
export function getSubscriptionStatus(account: AccountParam): SubscriptionStatus {
  const userSettings = account?.root?.userSettings;
  return (userSettings?.subscriptionStatus as SubscriptionStatus) ?? 'beta';
}

/**
 * Check if user is in beta mode
 */
export function isBetaUser(account: AccountParam): boolean {
  return getSubscriptionStatus(account) === 'beta';
}

/**
 * Get effective tier for limit calculations
 * During beta, all users get Plus tier limits regardless of actual tier.
 * Uses billing-shared getEffectiveTier logic.
 */
export function getEffectiveTier(account: AccountParam): SubscriptionTier {
  const tier = getSubscriptionTier(account);
  const status = getSubscriptionStatus(account);
  return getEffectiveTierFromBilling(tier, status);
}

/**
 * Get beta status message for display
 */
export function getBetaMessage(): string {
  return 'During beta, all users have Plus features free!';
}

/**
 * Get full subscription info (from Jazz cache)
 * Note: Limits are derived from effective tier (respects beta status).
 */
export function getSubscriptionInfo(account: AccountParam): SubscriptionInfo {
  const userSettings = account?.root?.userSettings;
  const tier = (userSettings?.subscriptionTier as SubscriptionTier) ?? 'free';
  const status = (userSettings?.subscriptionStatus as SubscriptionStatus) ?? 'beta';
  const effectiveTier = status === 'beta' ? 'plus' : tier;

  return {
    tier,
    status,
    endsAt: userSettings?.subscriptionEndsAt ?? null,
    limits: TIER_LIMITS[effectiveTier],
    syncedAt: userSettings?.subscriptionSyncedAt ?? null,
  };
}

/**
 * Check if subscription needs to be synced from backend
 */
export function needsSubscriptionSync(account: AccountParam): boolean {
  const userSettings = account?.root?.userSettings;
  const syncedAt = userSettings?.subscriptionSyncedAt;

  if (!syncedAt) return true;

  const elapsed = Date.now() - syncedAt;
  return elapsed > SYNC_INTERVAL_MS;
}

// ============================================================================
// Limit Checking
// ============================================================================

/**
 * Get the maximum number of lists allowed for the effective tier
 * (respects beta status)
 */
export function getMaxLists(account: AccountParam): number {
  const effectiveTier = getEffectiveTier(account);
  return TIER_LIMITS[effectiveTier].maxLists;
}

/**
 * Get the session retention period in days (-1 = unlimited)
 * (respects beta status)
 */
export function getSessionRetentionDays(account: AccountParam): number {
  const effectiveTier = getEffectiveTier(account);
  return TIER_LIMITS[effectiveTier].sessionRetentionDays;
}

/**
 * Count the total number of template folders (lists) the user has
 */
export function countUserLists(account: AccountParam): number {
  if (!account?.root?.folders) return 0;
  let count = 0;
  walkTree([...account.root.folders] as InstanceOfSchema<typeof FolderNode>[], (node) => {
    if (node.archived) return 'skip';
    if (isTemplateFolder(node)) count++;
  });
  return count;
}

/**
 * Check if user is at their list limit
 */
export function isAtListLimit(account: AccountParam): boolean {
  const maxLists = getMaxLists(account);
  if (maxLists === -1) return false; // Unlimited

  const currentCount = countUserLists(account);
  return currentCount >= maxLists;
}

/**
 * Check if user can create a new list
 */
export function canCreateList(account: AccountParam): boolean {
  return !isAtListLimit(account);
}

/**
 * Get the number of lists remaining before hitting the limit
 */
export function getListsRemaining(account: AccountParam): number {
  const maxLists = getMaxLists(account);
  if (maxLists === -1) return -1; // Unlimited

  const currentCount = countUserLists(account);
  return Math.max(0, maxLists - currentCount);
}

/**
 * Get usage as a percentage (0-100)
 */
export function getUsagePercentage(account: AccountParam): number {
  const maxLists = getMaxLists(account);
  if (maxLists === -1) return 0; // Unlimited = 0%

  const currentCount = countUserLists(account);
  return Math.min(100, Math.round((currentCount / maxLists) * 100));
}

/**
 * Check if user is approaching their limit (80%+ usage)
 */
export function isApproachingLimit(account: AccountParam): boolean {
  return getUsagePercentage(account) >= 80;
}

// ============================================================================
// Backend Sync
// ============================================================================

/**
 * Sync subscription status from backend to Jazz cache
 */
export async function syncSubscriptionFromBackend(account: AccountParam): Promise<void> {
  try {
    const response = await fetch('/api/billing/subscription', {
      credentials: 'include',
    });

    if (!response.ok) {
      console.warn('[subscription] Failed to fetch subscription from backend:', response.status);
      return;
    }

    const data = await response.json();
    const { subscription } = data;

    if (!subscription) {
      console.warn('[subscription] No subscription data in response');
      return;
    }

    // Update Jazz cache
    const userSettings = ensureUserSettings(account);
    userSettings.$jazz.set('subscriptionTier', subscription.tierSlug);
    userSettings.$jazz.set('subscriptionStatus', subscription.status);
    userSettings.$jazz.set(
      'subscriptionEndsAt',
      subscription.currentPeriodEnd ? subscription.currentPeriodEnd * 1000 : undefined,
    );
    userSettings.$jazz.set('maxLists', subscription.tier?.maxLists ?? TIER_LIMITS.free.maxLists);
    userSettings.$jazz.set(
      'sessionRetentionDays',
      subscription.tier?.sessionRetentionDays ?? TIER_LIMITS.free.sessionRetentionDays,
    );
    userSettings.$jazz.set('subscriptionSyncedAt', Date.now());

    if (import.meta.env.DEV)
      console.log('[subscription] Synced from backend:', subscription.tierSlug);
  } catch (error) {
    if (import.meta.env.DEV) console.error('[subscription] Error syncing from backend:', error);
  }
}

/**
 * Record current usage to backend (for analytics)
 */
export async function recordUsageToBackend(account: AccountParam): Promise<void> {
  try {
    const listCount = countUserLists(account);

    await fetch('/api/billing/usage', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ listCount }),
    });
  } catch {
    // Non-critical, don't log errors
  }
}

// ============================================================================
// Checkout & Portal
// ============================================================================

/**
 * Create a Stripe checkout session for upgrading
 */
export async function createCheckoutSession(tierSlug: 'plus' | 'premium'): Promise<string | null> {
  try {
    const response = await fetch('/api/billing/checkout', {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ tierSlug }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[subscription] Checkout error:', error);
      return null;
    }

    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error('[subscription] Error creating checkout session:', error);
    return null;
  }
}

/**
 * Create a Stripe billing portal session for managing subscription
 */
export async function createPortalSession(): Promise<string | null> {
  try {
    const response = await fetch('/api/billing/portal', {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('[subscription] Portal error:', error);
      return null;
    }

    const { url } = await response.json();
    return url;
  } catch (error) {
    console.error('[subscription] Error creating portal session:', error);
    return null;
  }
}

/**
 * Redirect to Stripe checkout for upgrading
 */
export async function redirectToCheckout(tierSlug: 'plus' | 'premium'): Promise<void> {
  const url = await createCheckoutSession(tierSlug);
  if (url) {
    window.location.href = url;
  }
}

/**
 * Redirect to Stripe billing portal
 */
export async function redirectToPortal(): Promise<void> {
  const url = await createPortalSession();
  if (url) {
    window.location.href = url;
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Ensure userSettings exists on the account
 */
function ensureUserSettings(account: AccountParam): InstanceOfSchema<typeof UserSettings> {
  if (!account?.root) {
    throw new Error('Account root not initialized');
  }

  if (!account.root.userSettings) {
    const userSettings = UserSettings.create(
      {
        enableAutoCategorization: true,
        subscriptionTier: 'free',
        subscriptionStatus: 'beta', // Default to beta during beta period
        maxLists: TIER_LIMITS.plus.maxLists, // Beta users get Plus limits
        sessionRetentionDays: TIER_LIMITS.plus.sessionRetentionDays,
      },
      { owner: account },
    );
    account.root.$jazz.set('userSettings', userSettings);
    return userSettings;
  }

  return account.root.userSettings;
}

// ============================================================================
// Display Helpers (CheckList-specific)
// ============================================================================

/**
 * Get tier price display (CheckList-specific, uses local TIERS config)
 */
export function getTierPrice(tier: SubscriptionTier): string {
  return TIERS[tier].priceDisplay;
}
